import { SourceContinuityTransport, type SourceContinuity, type SourceLane } from '../lib/gameState/sourceContinuity';
import { elapsedSince, localClockStamp, type LocalClockStamp } from '../lib/relayClock';
import { normalizeUsGameKey, usPeriodForMarketType, usGameSportForBoard, type UsGameSport } from '../lib/polymarketUs/sports';
import type { UsDiscoveryMarket } from '../lib/polymarketUs/marketClient';
import { usFullGameTotalFamily } from '../lib/polymarketUs/totalTarget';

/** Display-only quote. It is never account, rule, catalogue or execution evidence. */
export interface UsBrowserBoardQuote {
  marketSlug: string; gameKey: string; yesTeam: string; sport: UsGameSport;
  book: Pick<UsDiscoveryMarket['book'], 'observedAtMs' | 'yesBidUsd' | 'yesAskUsd' | 'noBidUsd' | 'noAskUsd'>;
  currentAt(): boolean;
}
export type UsBoardQuote = UsDiscoveryMarket | UsBrowserBoardQuote;
/** #1231 — display-only US full-game spread or total quote, with the catalogue
 *  fields the ticket's own joins read (`pickUsSpread`, `pickUsFullGameTotal`).
 *  `line` is the producer strike as exact text: the YES team's signed spread
 *  ("-3.5") or the total's threshold ("44.5"). Never account, rule, catalogue
 *  or execution evidence. */
export interface UsBrowserLineQuote {
  marketSlug: string; marketId: string; gameKey: string; sport: UsGameSport;
  marketType: 'spreads' | 'totals'; sportsMarketType: string; period: 'full';
  yesTeam: string | null; line: string;
  book: UsBrowserBoardQuote['book'];
  currentAt(): boolean;
}
type QuoteCore = { marketSlug: string; gameKey: string; sport: UsGameSport; book: UsBrowserBoardQuote['book']; currentAt(): boolean };
/** What an admission rule returns for an admitted row: the quote minus the shared core. */
type Identity<Q> = Omit<Q, 'marketSlug' | 'gameKey' | 'sport' | 'book' | 'currentAt'>;
const object = (v: unknown): Record<string, unknown> | null => v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : null;
const positive = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0;
const price = (v: unknown): string | null => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 100 ? String(v / 100) : null;
// Matches the existing worker's 45s transport watchdog; pings run every 30s.
export const US_BROWSER_TRANSPORT_LEASE_MS = 45_000;
interface Feed { connected: boolean; session: object; control: SourceContinuityTransport; alive: LocalClockStamp | null; ageMs: number }
interface Entry<Q> { feed: string; session: object; ticker: string; transportSport: string; raw: Record<string, unknown>; quote: Q; withdrawn: boolean }

/** Isolated public US board adapter behind marketStore's existing publish cadence.
 * The owner predicate is the fleet's existing per-pair admission decision.
 * No sockets, polling, timers, account data or generic-venue row conversion.
 * The feed authority (lease, lane, generation) is shared; each subclass names
 * which rows it admits. */
abstract class UsQuoteRows<Q extends QuoteCore> {
  private feeds = new Map<string, Feed>();
  protected rows = new Map<string, Entry<Q>>();
  constructor(protected readonly owns: (feed: string, sport: string, ticker: string) => boolean) {}
  /** The kind-specific half of admission; null refuses the row. */
  protected abstract admit(r: Record<string, unknown>, key: string, sport: UsGameSport): Identity<Q> | null;
  status(id: string, connected: boolean): void {
    const prior = this.feeds.get(id);
    if (connected && prior?.connected) return;
    this.feeds.set(id, { connected, session: {}, control: new SourceContinuityTransport(), alive: null, ageMs: 0 });
    for (const e of this.rows.values()) if (e.feed === id) e.withdrawn = true;
  }
  alive(id: string, ageMs = 0): void {
    const f = this.feeds.get(id);
    if (!f?.connected || !Number.isFinite(ageMs) || ageMs < 0) return;
    // A late heartbeat cannot resurrect rows whose lease already expired.
    if (f.alive && elapsedSince(f.alive) + f.ageMs >= US_BROWSER_TRANSPORT_LEASE_MS) {
      for (const e of this.rows.values()) if (e.feed === id) e.withdrawn = true;
    }
    f.alive = localClockStamp(); f.ageMs = ageMs;
  }
  control(id: string, frame: SourceContinuity | null, invalidLane?: SourceLane): void {
    const f = this.feeds.get(id);
    if (!f?.connected) return;
    if (frame && !frame.lane.startsWith('polyus-book:') || !frame && invalidLane && !invalidLane.startsWith('polyus-book:')) return;
    if (!frame) {
      if (invalidLane) f.control.quarantine(invalidLane); else f.control.quarantine(undefined);
      for (const e of this.rows.values()) if (e.feed === id && (!invalidLane || e.raw.lane === invalidLane)) e.withdrawn = true;
      return;
    }
    const prior = f.control.get(frame.lane);
    const result = f.control.receive(frame);
    if (result === 'ignored') return;
    if (result !== 'accepted' || frame.state !== 'valid' || prior && prior.sourceGen !== frame.sourceGen) {
      for (const e of this.rows.values()) if (e.feed === id && e.raw.lane === frame.lane) e.withdrawn = true;
    }
  }
  protected qualifies(e: Entry<Q>): boolean {
    const f = this.feeds.get(e.feed), r = e.raw;
    if (e.withdrawn || !f?.connected || f.session !== e.session || !f.alive
      || elapsedSince(f.alive) + f.ageMs >= US_BROWSER_TRANSPORT_LEASE_MS
      || f.control.invalidated || f.control.quarantinedLanes.includes(r.lane as SourceLane)
      || !positive(r.observedAtMs) || r.pendingResync !== undefined && r.pendingResync !== false
      || r.lane !== `polyus-book:${e.transportSport}` || !this.owns(e.feed, e.transportSport, e.ticker)) return false;
    const governing = f.control.get(r.lane as SourceLane);
    return governing?.state === 'valid' && governing.sourceEpoch === r.sourceEpoch && governing.sourceGen === r.sourceGen;
  }
  ingest(value: unknown, id: string): UsGameSport | null {
    const r = object(value), ticker = r?.ticker;
    if (!r || typeof ticker !== 'string' || !ticker.startsWith('PMUS-')) return null;
    const old = this.rows.get(ticker), slug = r.pmusMarketSlug;
    const transportSport = typeof r.feedSport === 'string' ? r.feedSport.toUpperCase() : typeof r.sport === 'string' ? r.sport.toUpperCase() : '';
    const base = typeof r.baseSport === 'string' ? r.baseSport : r.sport;
    const sport = typeof base === 'string' ? usGameSportForBoard(base) : null;
    const key = sport ? normalizeUsGameKey(r.canonicalGameKeyV2, sport) : null;
    const f = this.feeds.get(id);
    const identity = r.source !== 'polymarket_us' || r.venue !== 'polymarket_us' || !sport || !key || !key.startsWith(sport.toLowerCase()+'|')
      || typeof slug !== 'string' || !/^[A-Za-z0-9_-]{1,256}$/.test(slug) || ticker !== `PMUS-${slug}`
      || r.marketType !== 'game'
      || transportSport !== sport && transportSport !== `${sport}ALT`
      || r.status !== 'open' || !f?.connected ? null : this.admit(r, key, sport);
    if (!identity || !f || !sport || !key || typeof slug !== 'string') {
      if (old?.feed === id) old.withdrawn = true;
      return old?.quote.sport ?? null;
    }
    if (!old && this.rows.size >= 4096) return null;
    const quote = { marketSlug: slug, gameKey: key, sport, ...identity,
      book: { observedAtMs: positive(r.observedAtMs) ? r.observedAtMs : null,
        yesBidUsd: price(r.yesBid), yesAskUsd: price(r.yesAsk), noBidUsd: price(r.noBid), noAskUsd: price(r.noAsk) },
      currentAt: () => this.rows.get(ticker) === entry && this.qualifies(entry) } as unknown as Q;
    const entry: Entry<Q> = { ticker, feed: id, session: f.session, transportSport, raw: { ...r }, quote, withdrawn: false };
    // A delayed observation cannot roll back a quote within the same source.
    if (old?.feed === id && old.session === f.session && old.raw.sourceEpoch === r.sourceEpoch
      && old.raw.sourceGen === r.sourceGen && positive(old.raw.observedAtMs) && positive(r.observedAtMs)
      && r.observedAtMs < old.raw.observedAtMs) return null;
    // Retired-generation frames cannot replace a currently qualified quote.
    if (old && this.qualifies(old) && !this.qualifies(entry)) {
      if (old.feed === id && (r.sourceEpoch === undefined || r.sourceEpoch === old.raw.sourceEpoch)
        && (r.sourceGen === undefined || r.sourceGen === old.raw.sourceGen)) {
        old.withdrawn = true;
        return sport;
      }
      return null;
    }
    this.rows.set(ticker, entry); return sport;
  }
  book(id: string, ticker: string, value: unknown, observation: unknown): UsGameSport | null {
    const old = this.rows.get(ticker), book = object(value), evidence = object(observation);
    if (!old || old.feed !== id || !book || !evidence || book.ticker != null && book.ticker !== ticker) return null;
    const best = (levels: unknown): number | null | undefined => {
      if (!Array.isArray(levels)) return undefined;
      let top: number | null = null;
      for (const level of levels) {
        const l = object(level);
        if (!l || typeof l.price !== 'number' || !Number.isFinite(l.price) || l.price < 0 || l.price > 100
          || typeof l.size !== 'number' || !Number.isFinite(l.size) || l.size < 0) return undefined;
        if (l.size > 0 && (top === null || l.price > top)) top = l.price;
      }
      return top;
    };
    const yes = best(book.yes), no = best(book.no);
    if (yes === undefined || no === undefined) return this.withdraw(id, ticker);
    const complement = (n: number) => Math.round((100 - n) * 1e8) / 1e8;
    return this.ingest({ ...old.raw, ...evidence, yesBid: yes, noBid: no,
      yesAsk: no === null ? null : complement(no), noAsk: yes === null ? null : complement(yes) }, id);
  }
  withdraw(id: string, ticker: string): UsGameSport | null {
    const e = this.rows.get(ticker);
    if (!e || e.feed !== id) return null;
    e.withdrawn = true; return e.quote.sport;
  }
  remove(id: string, ticker: string): void { if (this.rows.get(ticker)?.feed === id) this.rows.delete(ticker); }
  reset(id: string, passes: (raw: Record<string, unknown>) => boolean): void {
    for (const [ticker, e] of this.rows) if (e.feed === id && !passes(e.raw)) this.rows.delete(ticker);
  }
  retireUnowned(): void {
    for (const e of this.rows.values()) if (!this.owns(e.feed, e.transportSport, e.ticker)) e.withdrawn = true;
  }
  retainFeeds(ids: readonly string[]): void {
    const live = new Set(ids);
    for (const id of this.feeds.keys()) if (!live.has(id)) this.feeds.delete(id);
    for (const [ticker, e] of this.rows) if (!live.has(e.feed)) this.rows.delete(ticker);
  }
  /** #1231 identity lookup: the US game key of an owned row for this slug,
   *  whatever its freshness (identity, not price evidence). */
  gameKeyForSlug(slug: string): string | null {
    const e = this.rows.get(`PMUS-${slug}`);
    return e && this.owns(e.feed, e.transportSport, e.ticker) ? e.quote.gameKey : null;
  }
}

/** The full-game moneyline quotes the board and Game Detail show. */
export class UsBoardQuotes extends UsQuoteRows<UsBrowserBoardQuote> {
  protected admit(r: Record<string, unknown>, key: string): Identity<UsBrowserBoardQuote> | null {
    const team = r.canonicalYesTeam;
    if (r.pmusMarketType !== 'moneyline'
      || typeof r.sportsMarketType !== 'string' || usPeriodForMarketType(r.sportsMarketType) !== 'full'
      || r.period !== undefined && r.period !== 'full'
      || typeof team !== 'string' || !key.split('|').slice(3).includes(team)) return null;
    return { yesTeam: team };
  }
  quotes(sports: readonly UsGameSport[]): ReadonlyMap<string, UsBrowserBoardQuote> {
    return this.owned(e => sports.includes(e.quote.sport));
  }
  /** One game's quote (US canonical game key) under the same owner and
   *  ambiguity rule as `quotes()`. Still one pass over every row, not an
   *  index lookup; it only skips building a per-sport map. */
  quoteForGame(gameKey: string): UsBrowserBoardQuote | null {
    return this.owned(e => e.quote.gameKey === gameKey).get(gameKey) ?? null;
  }
  // Owned rows by game key; a key two owned rows claim is dropped, never guessed.
  private owned(match: (e: Entry<UsBrowserBoardQuote>) => boolean): Map<string, UsBrowserBoardQuote> {
    const result = new Map<string, UsBrowserBoardQuote>(), ambiguous = new Set<string>();
    for (const e of this.rows.values()) {
      if (!match(e) || !this.owns(e.feed, e.transportSport, e.ticker)) continue;
      const key = e.quote.gameKey;
      if (result.has(key)) ambiguous.add(key); else result.set(key, e.quote);
    }
    for (const key of ambiguous) result.delete(key);
    return result;
  }
}

/** Exact strike text for a finite producer number with at most two decimals
 *  (the relay's grammar, coreFeed.ts), or null. */
const strikeText = (v: unknown): string | null => {
  if (typeof v !== 'number' || !Number.isFinite(v) || Math.abs(v) > 1000) return null;
  const t = String(v);
  return /^-?[0-9]{1,4}(\.[0-9]{1,2})?$/.test(t) ? t : null;
};

/** #1231 — US full-game spreads and supported native totals, under the same
 *  feed authority as the moneyline quote. Period lines, team totals, props and
 *  families G1 does not support are refused, never guessed. */
export class UsLineQuotes extends UsQuoteRows<UsBrowserLineQuote> {
  protected admit(r: Record<string, unknown>, key: string, sport: UsGameSport): Identity<UsBrowserLineQuote> | null {
    const marketId = r.pmusMarketId, type = r.sportsMarketType, line = strikeText(r.floorStrike);
    if (typeof marketId !== 'string' || !/^[0-9]{1,32}$/.test(marketId) || typeof type !== 'string'
      || r.period !== 'full' || line === null) return null;
    if (r.pmusMarketType === 'spreads') {
      const team = r.canonicalYesTeam;
      if (!/full_game_spread$/.test(type) || typeof team !== 'string' || !key.split('|').slice(3).includes(team)) return null;
      return { marketId, marketType: 'spreads', sportsMarketType: type, period: 'full', yesTeam: team, line };
    }
    if (r.pmusMarketType === 'totals') {
      if (!usFullGameTotalFamily(type, sport) || r.canonicalYesTeam != null || !(Number(line) > 0)) return null;
      return { marketId, marketType: 'totals', sportsMarketType: type, period: 'full', yesTeam: null, line };
    }
    return null;
  }
  /** Owned line quotes for one US game, current or not (a non-current one is
   *  shown disabled with its reason, not as absent). Ambiguity is the picks'
   *  call, never this. */
  lineCandidates(gameKey: string): { spreads: UsBrowserLineQuote[]; totals: UsBrowserLineQuote[] } {
    const spreads: UsBrowserLineQuote[] = [], totals: UsBrowserLineQuote[] = [];
    for (const e of this.rows.values()) {
      if (e.quote.gameKey !== gameKey || !this.owns(e.feed, e.transportSport, e.ticker)) continue;
      (e.quote.marketType === 'spreads' ? spreads : totals).push(e.quote);
    }
    return { spreads, totals };
  }
  /** US game keys holding at least one owned line row. SLATE GAMES ONLY: the
   *  board declares US tickers only for games the K/Intl index names, so a
   *  US-only game never appears here; US-only games need a feed-needs change. */
  gamesWithLines(sports: readonly UsGameSport[]): ReadonlySet<string> {
    const games = new Set<string>();
    for (const e of this.rows.values()) {
      if (sports.includes(e.quote.sport) && this.owns(e.feed, e.transportSport, e.ticker)) games.add(e.quote.gameKey);
    }
    return games;
  }
}

/** #1231 — slug → US game key for every full-game moneyline, spread and total
 *  the board's US index read names (identity only: no prices). Each source's
 *  successful read replaces its entries; a failed read leaves them. */
export class UsSlugIdentity {
  private sources = new Map<string, Map<string, string>>();
  set(sourceId: string, rows: readonly unknown[]): void {
    const slugs = new Map<string, string>();
    for (const row of rows) {
      const r = object(row), slug = r?.pmusMarketSlug, type = r?.pmusMarketType;
      if (!r || r.source !== 'polymarket_us' || r.venue !== 'polymarket_us' || r.marketType !== 'game'
        || r.status !== 'open' || typeof slug !== 'string' || !/^[A-Za-z0-9_-]{1,256}$/.test(slug)
        || r.ticker !== `PMUS-${slug}` || typeof r.sportsMarketType !== 'string') continue;
      const base = typeof r.baseSport === 'string' ? r.baseSport : r.sport;
      const sport = typeof base === 'string' ? usGameSportForBoard(base) : null;
      const key = sport ? normalizeUsGameKey(r.canonicalGameKeyV2, sport) : null;
      if (!sport || !key || !key.startsWith(sport.toLowerCase() + '|')) continue;
      const full = type === 'moneyline'
        ? usPeriodForMarketType(r.sportsMarketType) === 'full' && (r.period === undefined || r.period === 'full')
        : (type === 'spreads' && /full_game_spread$/.test(r.sportsMarketType)
          || type === 'totals' && usFullGameTotalFamily(r.sportsMarketType, sport) !== null) && r.period === 'full';
      if (full) slugs.set(slug, key);
    }
    this.sources.set(sourceId, slugs);
  }
  clear(sourceId: string): void { this.sources.delete(sourceId); }
  /** Whether any source has published a read (then it is the authority). */
  published(): boolean { return this.sources.size > 0; }
  get(slug: string): string | null {
    for (const slugs of this.sources.values()) { const key = slugs.get(slug); if (key) return key; }
    return null;
  }
}

/** #1231 / #1232 — the US canonical game key for a full-game US moneyline,
 *  spread or total slug. Once the board's identity index has been published it
 *  is the authority (a slug the latest successful read dropped answers null);
 *  before the first read, the two quote stores answer. Identity only: a stale,
 *  withdrawn or since-CLOSED market still answers until a source drops it, so a
 *  non-null answer does not mean the market is open. Null for an unknown slug,
 *  a prop, team-total or period market, or a game not on the slate.
 *  Spread/total identity is published only while the board is on the
 *  Spread/Total reading; on the Moneyline reading only moneyline slugs answer. */
export function gameKeyForUsSlug(slug: string, moneyline: UsBoardQuotes, lines: UsLineQuotes,
  identity: UsSlugIdentity): string | null {
  if (identity.published()) return identity.get(slug);
  return moneyline.gameKeyForSlug(slug) ?? lines.gameKeyForSlug(slug);
}
